import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/formatError";
import { Loader2, MessageSquare, ThumbsUp, Pin, Lock, Bookmark, BookmarkCheck, Megaphone, Plus, Send, Search } from "lucide-react";
import { UniHeader } from "./_lmsShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university`;

const SCOPES = [
  { key: "university", label: "All University" },
  { key: "program", label: "Program" },
  { key: "course", label: "Course" },
  { key: "residency", label: "Residency" },
  { key: "ce", label: "CE Calendar" },
  { key: "alumni", label: "Alumni" },
];
const POST_TYPES = [
  { key: "general", label: "General Discussion" },
  { key: "qa", label: "Q & A" },
  { key: "assignment", label: "Assignment Discussion" },
  { key: "case_study", label: "Case Study Discussion" },
  { key: "clinical_case", label: "Clinical Case Review" },
];

export default function UniversityCommunity() {
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") || "university";
  const scopeId = params.get("scope_id") || "";
  const search = params.get("q") || "";
  const me = (localStorage.getItem("kcis_student_email") || "").toLowerCase();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/community/posts`, {
        params: { scope, scope_id: scopeId, q: search, limit: 50 },
      });
      setPosts(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load community"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope, scopeId, search]);

  const setQuery = (k, v) => {
    const p = new URLSearchParams(params); p.set(k, v); setParams(p);
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="KISSED University Community" back="/university/student" />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="uni-community-page">
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-2">
          <MessageSquare className="w-3.5 h-3.5" />Discussion
        </div>
        <h1 className="font-display text-3xl text-slate-900">Community</h1>
        <p className="text-sm text-slate-600 mt-1">Engage with classmates, instructors, and alumni. Participation earns points toward your final-exam eligibility.</p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <select value={scope} onChange={(e) => setQuery("scope", e.target.value)} className="rounded-xl border border-slate-200 h-9 px-2 text-sm" data-testid="uni-community-scope">
            {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <input value={scopeId} onChange={(e) => setQuery("scope_id", e.target.value)} placeholder="Scope ID (course code / program / event)" className="rounded-xl border border-slate-200 h-9 px-2 text-sm font-mono w-64" data-testid="uni-community-scope-id" />
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setQuery("q", e.target.value)} placeholder="Search posts…" className="rounded-xl border border-slate-200 h-9 pl-8 pr-2 text-sm w-72" data-testid="uni-community-search" />
          </div>
          <button onClick={() => setComposing(true)} className="ml-auto rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="uni-community-new">
            <Plus className="w-4 h-4" />New post
          </button>
        </div>

        {composing && (
          <Composer
            scope={scope} scopeId={scopeId} authorEmail={me}
            onClose={() => setComposing(false)}
            onCreated={() => { setComposing(false); load(); }}
          />
        )}

        {loading ? (
          <div className="mt-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : posts.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No posts here yet. Be the first to start a discussion.
          </div>
        ) : (
          <ul className="mt-6 space-y-3">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} me={me} reload={load} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function PostCard({ post, me, reload }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`uni-post-${post.id}`}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        {post.is_announcement && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full"><Megaphone className="w-3 h-3" />Announcement</span>}
        {post.is_pinned && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><Pin className="w-3 h-3" />Pinned</span>}
        {post.is_locked && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" />Locked</span>}
        {!post.is_announcement && (
          <span className="text-[10px] uppercase tracking-wide text-purple-900 bg-purple-50 px-2 py-0.5 rounded-full">
            {(post.post_type || "general").replace(/_/g, " ")}
          </span>
        )}
        {post.scope_id && <span className="text-[10px] text-slate-500 font-mono">· {post.scope_id}</span>}
      </div>
      <button onClick={() => setExpanded((v) => !v)} className="text-left w-full">
        <div className="font-display text-lg text-slate-900">{post.title}</div>
        <div className="text-sm text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{post.body}</div>
      </button>
      <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
        <span>by <b className="text-slate-900">{post.author_name || post.author_email}</b></span>
        <span>·</span>
        <span>{(post.created_at || "").slice(0, 10)}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{post.reply_count || 0}</span>
        <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{post.reaction_count || 0}</span>
      </div>
      {expanded && <Thread postId={post.id} me={me} reload={reload} />}
    </li>
  );
}

function Thread({ postId, me, reload }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [caseDiscussion, setCaseDiscussion] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/community/posts/${postId}`, { params: { viewer_email: me } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e, "Could not load thread")); }
  };
  useEffect(() => { load(); }, [postId]);

  const reply = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/community/posts/${postId}/replies?author_email=${encodeURIComponent(me)}`,
        { post_id: postId, body: text.trim(), case_discussion: caseDiscussion });
      setText(""); setCaseDiscussion(false);
      load(); reload?.();
      toast.success("Reply posted");
    } catch (e) { toast.error(formatApiError(e, "Reply failed")); }
    finally { setSending(false); }
  };

  const react = async (kind) => {
    try {
      await axios.post(`${API}/community/reactions?author_email=${encodeURIComponent(me)}`,
        { target_type: "post", target_id: postId, reaction: kind });
      load();
    } catch (e) { toast.error(formatApiError(e, "Reaction failed")); }
  };

  const bookmark = async () => {
    try {
      await axios.post(`${API}/community/posts/${postId}/bookmark?author_email=${encodeURIComponent(me)}`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Bookmark failed")); }
  };

  if (!data) return <div className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>;
  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`uni-thread-${postId}`}>
      <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.post?.body}</div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={() => react("like")} className="text-xs px-2 py-1 rounded-full border border-slate-200 hover:border-purple-300 inline-flex items-center gap-1" data-testid={`uni-react-${postId}`}><ThumbsUp className="w-3 h-3" />Like</button>
        <button onClick={() => react("helpful")} className="text-xs px-2 py-1 rounded-full border border-slate-200 hover:border-purple-300">Helpful</button>
        <button onClick={() => react("insightful")} className="text-xs px-2 py-1 rounded-full border border-slate-200 hover:border-purple-300">Insightful</button>
        <button onClick={bookmark} className="text-xs px-2 py-1 rounded-full border border-slate-200 hover:border-purple-300 inline-flex items-center gap-1" data-testid={`uni-bookmark-${postId}`}>
          {data.bookmarked ? <BookmarkCheck className="w-3 h-3 text-purple-900" /> : <Bookmark className="w-3 h-3" />}
          {data.bookmarked ? "Saved" : "Save"}
        </button>
      </div>

      {/* Replies */}
      <ul className="mt-4 space-y-2">
        {(data.replies || []).map((r) => (
          <li key={r.id} className={`rounded-xl border p-3 ${r.is_instructor ? "border-purple-200 bg-purple-50/40" : "border-slate-200 bg-slate-50"}`} data-testid={`uni-reply-${r.id}`}>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="font-medium text-slate-900">{r.author_name || r.author_email}</span>
              {r.is_instructor && <span className="text-[10px] uppercase tracking-wide text-purple-900 bg-purple-100 px-1.5 py-0.5 rounded-full">Instructor</span>}
              <span>· {(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="text-sm text-slate-800 mt-1.5 whitespace-pre-wrap">{r.body}</div>
          </li>
        ))}
      </ul>

      {!data.post?.is_locked && me && (
        <div className="mt-4">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
            placeholder="Add a reply…"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            data-testid={`uni-reply-text-${postId}`} />
          <div className="mt-2 flex items-center justify-between">
            <label className="text-[11px] text-slate-600 inline-flex items-center gap-1.5">
              <input type="checkbox" checked={caseDiscussion} onChange={(e) => setCaseDiscussion(e.target.checked)} />
              Tag as Case Discussion contribution (+10 pts)
            </label>
            <button onClick={reply} disabled={sending || !text.trim()} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-60" data-testid={`uni-reply-send-${postId}`}>
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Composer({ scope, scopeId, authorEmail, onClose, onCreated }) {
  const [form, setForm] = useState({
    scope, scope_id: scopeId, post_type: "general", title: "", body: "",
    resource_shared: false, case_discussion: false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!authorEmail) return toast.error("Please login as a student to post.");
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body are required");
    setSaving(true);
    try {
      await axios.post(`${API}/community/posts?author_email=${encodeURIComponent(authorEmail)}`, form);
      toast.success("Post created" + (form.resource_shared ? " · +5 resource share pts" : "") + (form.case_discussion ? " · +10 case discussion pts" : ""));
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e, "Could not create post"));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-purple-200 bg-purple-50/30 p-5" data-testid="uni-community-composer">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">New post</div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <select value={form.post_type} onChange={(e) => set("post_type", e.target.value)} className="md:col-span-3 rounded-xl border border-slate-200 h-9 px-2 text-sm">
          {POST_TYPES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Post title" className="md:col-span-9 rounded-xl border border-slate-200 h-9 px-2 text-sm" data-testid="uni-post-title" />
      </div>
      <textarea rows={5} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="What would you like to share?" className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="uni-post-body" />
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.resource_shared} onChange={(e) => set("resource_shared", e.target.checked)} />Resource share (+5 pts)</label>
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.case_discussion} onChange={(e) => set("case_discussion", e.target.checked)} />Case discussion (+10 pts)</label>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onClose} className="text-xs text-slate-500 hover:underline">Cancel</button>
          <button onClick={save} disabled={saving} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="uni-post-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Post
          </button>
        </div>
      </div>
    </div>
  );
}
